import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import api from "@/lib/api";
import { Search, MapPin, Cable, Zap, Factory, Wrench, FlaskConical, PencilRuler, ShieldCheck, Gauge, Cpu, ArrowRight, CheckCircle2, TrendingUp, Quote, Mail, Phone } from "lucide-react";

const HERO_PORTRAIT = "https://customer-assets-39nsmqrw.emergentagent.net/job_cable-jobs-pro/artifacts/xpgiyuui_image.png";

const CATEGORIES = [
  { icon: Zap, name: "HV / EHV Power Cables", sub: "LT · HT · EHT · XLPE" },
  { icon: Cable, name: "Conductor Manufacturing", sub: "ACSR · AAAC · HTLS · ABC" },
  { icon: Factory, name: "Wire Drawing & Stranding", sub: "Rod Breakdown · Drum Twister" },
  { icon: PencilRuler, name: "Extrusion & CV Line", sub: "Insulation · Cross-linking" },
  { icon: Gauge, name: "Quality & HV Testing", sub: "PD · Tan Delta · Type Testing" },
  { icon: Wrench, name: "Plant Maintenance", sub: "TPM · Preventive Maintenance" },
  { icon: FlaskConical, name: "R&D & Cable Design", sub: "IS · IEC · ASTM Standards" },
  { icon: Cpu, name: "Automation & PPC", sub: "PLC · SCADA · Production Planning" },
];

const WHY = [
  { icon: ShieldCheck, title: "Industry-Specific Talent", desc: "Every profile is tagged with cable products, conductor types and technical processes." },
  { icon: TrendingUp, title: "Smart Candidate Matching", desc: "Weighted match scores rank candidates against your exact hiring requirement." },
  { icon: Search, title: "Advanced Recruiter Search", desc: "Filter by experience, XLPE/HT expertise, CV line, stranding and 20+ parameters." },
  { icon: Factory, title: "Specialized Technical Profiles", desc: "Purpose-built for cable, wire, conductor and power transmission manufacturing." },
  { icon: CheckCircle2, title: "Verified Companies", desc: "Employer accounts are verified so candidates apply with confidence." },
  { icon: Zap, title: "Faster Hiring", desc: "Requirement-driven shortlisting cuts weeks off your specialist hiring cycle." },
];

export default function Home() {
  const navigate = useNavigate();
  const [kw, setKw] = useState("");
  const [loc, setLoc] = useState("");
  const [stats, setStats] = useState({ candidates: 0, jobs: 0, companies: 0 });

  useEffect(() => {
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const doSearch = () => {
    const p = new URLSearchParams();
    if (kw) p.set("keyword", kw);
    if (loc) p.set("location", loc);
    navigate(`/jobs?${p.toString()}`);
  };

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-40" />
        <img src="https://images.unsplash.com/photo-1555963966-b7ae5404b6ed?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-20 hidden lg:block" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-mono-tech tracking-widest uppercase text-[#D96B27] bg-[#D96B27]/10 border border-[#D96B27]/30 px-3 py-1 rounded-full mb-6 animate-fade-up">Cable · Wire · Conductor · Power Transmission</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] animate-fade-up">
              Find Specialized Talent in the <span className="text-[#D96B27]">Cable & Conductor</span> Industry
            </h1>
            <p className="mt-5 text-lg text-slate-300 max-w-2xl animate-fade-up">
              The dedicated recruitment marketplace connecting engineers, technicians and managers with India's leading cable, wire and conductor manufacturers.
            </p>

            <div className="mt-8 bg-white rounded-xl p-2 flex flex-col sm:flex-row gap-2 shadow-2xl max-w-2xl animate-fade-up">
              <div className="flex items-center gap-2 flex-1 px-3">
                <Search className="w-5 h-5 text-slate-400 shrink-0" />
                <input data-testid="hero-search-keyword" value={kw} onChange={(e) => setKw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} placeholder="Job title, skill or product (e.g. XLPE)" className="w-full py-2.5 text-slate-800 outline-none text-sm" />
              </div>
              <div className="flex items-center gap-2 sm:w-52 px-3 sm:border-l border-slate-200">
                <MapPin className="w-5 h-5 text-slate-400 shrink-0" />
                <input data-testid="hero-search-location" value={loc} onChange={(e) => setLoc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} placeholder="Location" className="w-full py-2.5 text-slate-800 outline-none text-sm" />
              </div>
              <button data-testid="hero-search-btn" onClick={doSearch} className="copper-gradient text-white font-semibold px-6 py-2.5 rounded-lg hover:opacity-95 transition-opacity">Search Jobs</button>
            </div>

            <div className="mt-6 flex flex-wrap gap-3 animate-fade-up">
              <Link to="/login?role=candidate" data-testid="hero-create-profile" className="inline-flex items-center gap-2 text-sm font-semibold bg-white/10 hover:bg-white/20 border border-white/20 px-5 py-2.5 rounded-lg transition-colors">Create Profile <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/login?role=employer" data-testid="hero-post-job" className="inline-flex items-center gap-2 text-sm font-semibold hover:text-[#D96B27] px-2 py-2.5 transition-colors">Post a Job</Link>
            </div>
          </div>
        </div>

        <div className="relative border-t border-white/10 bg-slate-900/60 backdrop-blur">
          <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-3 gap-4 text-center">
            {[["Specialist Candidates", stats.candidates + "+"], ["Live Jobs", stats.jobs + "+"], ["Verified Companies", stats.companies + "+"]].map(([l, v]) => (
              <div key={l}>
                <div className="font-display text-2xl sm:text-3xl font-extrabold text-[#D96B27]">{v}</div>
                <div className="text-xs sm:text-sm text-slate-400 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Explore by Industry Domain</h2>
        <p className="text-slate-500 mt-2">Specialized sectors across cable & conductor manufacturing.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {CATEGORIES.map((c, i) => (
            <button key={c.name} data-testid={`category-${i}`} onClick={() => navigate(`/jobs?keyword=${encodeURIComponent(c.name.split(" ")[0])}`)}
              className="group text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-[#D96B27] hover:shadow-md transition-all">
              <div className="w-11 h-11 rounded-lg bg-[#FFF3EC] flex items-center justify-center mb-3 group-hover:copper-gradient transition-all">
                <c.icon className="w-5 h-5 text-[#D96B27] group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-display font-semibold text-slate-900 text-sm leading-snug">{c.name}</h3>
              <p className="text-xs text-slate-400 mt-1 font-mono-tech">{c.sub}</p>
            </button>
          ))}
        </div>
      </section>

      {/* WHY */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Why CableCareer?</h2>
          <p className="text-slate-500 mt-2 max-w-2xl">Generic job boards can't tell an XLPE CV line engineer from a bare conductor stranding specialist. We can.</p>
          <div className="grid md:grid-cols-3 gap-5 mt-8">
            {WHY.map((w) => (
              <div key={w.title} className="p-6 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors">
                <div className="w-10 h-10 rounded-lg copper-gradient flex items-center justify-center mb-4">
                  <w.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-display font-semibold text-slate-900">{w.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CABLE HERO — O.P. Tiwari */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="flex items-center gap-2 mb-8">
            <span className="h-px w-8 bg-[#D96B27]" />
            <span className="text-xs font-mono-tech tracking-widest uppercase text-[#D96B27]">The Cable Hero</span>
          </div>
          <div className="grid lg:grid-cols-[380px_1fr] gap-10 items-center">
            <div className="relative mx-auto lg:mx-0" data-testid="cable-hero-portrait">
              <div className="absolute -inset-3 copper-gradient rounded-2xl opacity-20 blur-xl" />
              <div className="absolute inset-0 hero-grid rounded-2xl" />
              <img src={HERO_PORTRAIT} alt="Mr. O.P. Tiwari" className="relative rounded-2xl w-full max-w-[360px] object-cover shadow-2xl border-4 border-white" />
              <div className="relative -mt-8 ml-4 inline-block bg-slate-900 text-white rounded-xl px-5 py-3 shadow-lg">
                <div className="font-display font-bold text-lg leading-tight">Mr. O.P. Tiwari</div>
                <div className="text-xs text-[#D96B27] font-mono-tech">Strategic Advisor</div>
                <div className="mt-1.5 space-y-0.5">
                  <a href="mailto:omptiwari1954@gmail.com" data-testid="hero-email" className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-[#D96B27] transition-colors"><Mail className="w-3 h-3" />omptiwari1954@gmail.com</a>
                  <a href="tel:+919904285444" data-testid="hero-phone" className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-[#D96B27] transition-colors"><Phone className="w-3 h-3" />+91 99042 85444</a>
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 leading-tight">
                A legacy built on wire, cable & conductor excellence
              </h2>
              <p className="mt-3 text-slate-500">Helping employees and employers find each other across the power cable manufacturing industry.</p>

              <p className="mt-5 text-base text-slate-700 leading-relaxed border-l-4 border-[#D96B27] pl-4" data-testid="cable-hero-intro">
                With <span className="font-semibold text-slate-900">45 years of diverse experience</span> in the wire and power cable industry, spanning roles from project management to operations, and having worked with leading global consulting firms like <span className="font-semibold text-slate-900">Bain, McKinsey, A.T. Kearney and KPMG</span>, Mr. O.P. Tiwari has developed deep expertise in the field. This extensive background equips him with a strong understanding of both strategic growth and operational execution, enabling him to navigate industry challenges and market disruptions effectively.
              </p>

              <div className="relative mt-6 bg-slate-50 rounded-2xl p-6 border border-slate-100">
                <Quote className="absolute -top-3 left-6 w-8 h-8 text-[#D96B27] bg-white rounded-full p-1.5 shadow" />
                <div className="space-y-4 text-slate-600 text-base leading-relaxed">
                  <p>With a deep commitment to advancing the power cable manufacturing industry, my focus as <span className="font-semibold text-slate-800">Strategic Advisor at Torrent Electricals</span> revolves around harnessing problem-solving strategies to drive business growth and operational efficiency. At <span className="font-semibold text-slate-800">DICABS</span>, my tenure as <span className="font-semibold text-slate-800">Executive Director</span> was marked by a dedication to manufacturing process improvement, directly contributing to enhanced production workflows and new business development.</p>
                  <p>In these roles, my approach has always been to foster innovation and efficiency. Leading by example, my efforts facilitated the creation of robust solutions to complex challenges, establishing a track record of success in new ventures and operational advancements. This journey reflects my enduring mission to deliver excellence and propel organizations toward a sustainable and prosperous future.</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                {[["45+ Years", "Wire & Power Cable"], ["Bain · McKinsey", "A.T. Kearney · KPMG"], ["Strategic Advisor", "Industry Veteran"], ["Project Mgmt to", "Operations"]].map(([t, s]) => (
                  <div key={t} className="border border-slate-200 rounded-lg px-4 py-2">
                    <div className="font-display font-semibold text-slate-900 text-sm">{t}</div>
                    <div className="text-xs text-slate-400 font-mono-tech">{s}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="copper-gradient rounded-2xl p-10 sm:p-14 text-white flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold">Ready to build your specialist team?</h2>
            <p className="mt-2 text-white/90">Join the cable & conductor industry's dedicated hiring platform.</p>
          </div>
          <div className="flex gap-3 shrink-0">
            <Link to="/login?role=candidate" className="bg-white text-[#C05615] font-semibold px-6 py-3 rounded-lg hover:bg-slate-100 transition-colors">Create Profile</Link>
            <Link to="/login?role=employer" className="bg-slate-900 text-white font-semibold px-6 py-3 rounded-lg hover:bg-slate-800 transition-colors">Post a Job</Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
